import { RJSFSchema, UiSchema } from "@rjsf/utils";
import { useIntl } from "react-intl";

import { IdentityProviderType, License } from "@nens/rana/api-v1";
import { capitalizeFirst, objectValues } from "@nens/shared-utils";

import { TenantCreateOrEditProps } from "../admin-tenant-create-or-edit";

export const useTenantCreateOrEditFormSchema = ({ type }: TenantCreateOrEditProps) => {
  const intl = useIntl();

  const schema: RJSFSchema = {
    type: "object",
    required: ["id", "name", "identityProviders", "threediOrganisations", "license"],
    properties: {
      id: {
        type: "string",
        title: intl.formatMessage({ id: "column.id", defaultMessage: "Organisation ID" }),
        readOnly: type === "editing",
        description: "Be careful, this is the URL to the organisation and cannot be changed later",
      },
      name: {
        type: "string",
        title: intl.formatMessage({ id: "column.name", defaultMessage: "Name" }),
        description: "The human-readable name of the organisation",
      },
      description: {
        type: ["string", "null"],
        title: intl.formatMessage({
          id: "column.description",
          defaultMessage: "Description",
        }),
        description: "A short description of the organisation",
      },
      threediOrganisations: {
        type: "array",
        title: intl.formatMessage({
          id: "column.threedi_organisations",
          defaultMessage: "Rana HCC organisations",
        }),
        description: "Select the Rana HCC organisation for this Rana organisation",
        items: {
          type: "string",
        },
        minItems: 1,
      },
      identityProviders: {
        type: "array",
        title: intl.formatMessage({
          id: "column.identity_providers",
          defaultMessage: "Identity Providers",
        }),
        description: "Select the identity provider(s) for this organisation",
        items: {
          type: "object",
          required: ["id", "name"],
          properties: {
            id: {
              type: "string",
              title: intl.formatMessage({
                id: "column.identity_provider.id",
                defaultMessage: "Provider ID",
              }),
            },
            name: {
              type: "string",
              title: intl.formatMessage({
                id: "column.identity_provider.name",
                defaultMessage: "Provider Name",
              }),
            },
            type: {
              type: "string",
              title: intl.formatMessage({
                id: "column.identity_provider.type",
                defaultMessage: "Provider Type",
              }),
              enum: objectValues(IdentityProviderType),
            },
          },
        },
      },
      license: {
        type: "string",
        title: intl.formatMessage({
          id: "column.license",
          defaultMessage: "License",
        }),
        description: "Select the license type for this organisation",
        enum: objectValues(License),
        default: License.Pioneer,
      },
    },
  };

  const uiSchema: UiSchema = {
    id: {
      "ui:placeholder": intl.formatMessage({
        id: "column.id.placeholder",
        defaultMessage: "Enter organisation ID",
      }),
    },
    name: {
      "ui:placeholder": intl.formatMessage({
        id: "column.name.placeholder",
        defaultMessage: "Enter name",
      }),
    },
    description: {
      "ui:placeholder": intl.formatMessage({
        id: "column.description.placeholder",
        defaultMessage: "Enter description",
      }),
      "ui:widget": "textarea",
      "ui:emptyValue": null,
    },
    threediOrganisations: {
      "ui:placeholder": intl.formatMessage({
        id: "column.threedi_organisations.placeholder",
        defaultMessage: "Enter Rana HCC organisations",
      }),
    },
    identityProviders: {
      "ui:placeholder": intl.formatMessage({
        id: "column.identity_providers.placeholder",
        defaultMessage: "Enter identity providers",
      }),
      "ui:options": { label: true },
      items: {
        id: {
          "ui:placeholder": intl.formatMessage({
            id: "column.identity_provider.id.placeholder",
            defaultMessage: "Enter provider ID",
          }),
        },
        name: {
          "ui:placeholder": intl.formatMessage({
            id: "column.identity_provider.name.placeholder",
            defaultMessage: "Enter provider name",
          }),
        },
        type: {
          "ui:placeholder": intl.formatMessage({
            id: "column.identity_provider.type.placeholder",
            defaultMessage: "Enter provider type",
          }),
          "ui:enumNames": objectValues(IdentityProviderType).map(capitalizeFirst),
        },
      },
    },
    license: {
      "ui:placeholder": intl.formatMessage({
        id: "column.license.placeholder",
        defaultMessage: "Select license type",
      }),
      "ui:enumNames": objectValues(License).map(capitalizeFirst),
    },
  };

  return { schema, uiSchema };
};
